import { motion, Variants } from 'framer-motion';
import { BaseButton, BaseButtonProps } from '../base';

interface BackButtonProps extends BaseButtonProps {
  icon?: React.ElementType;
  reverse?: boolean;
}

export default ({
  className = '',
  reverse = false,
  ...props
}: Partial<BackButtonProps>) => {
  // 1. Define motion variants for the primary (exiting) arrow
  const primaryVariants: Variants = {
    rest: { x: 0, opacity: 1 },
    hover: {
      // If reversed, slide right. Otherwise, slide left.
      x: '-100%',
      opacity: 0,
      transition: { duration: 0.3, ease: 'easeInOut' },
    },
  };

  // 2. Define motion variants for the secondary (entering) arrow
  const secondaryVariants: Variants = {
    rest: {
      // If reversed, start off-screen left. Otherwise, start off-screen right.
      x: '100%',
      opacity: 0,
    },
    hover: {
      x: 0,
      opacity: 1,
      transition: { duration: 0.3, ease: 'easeInOut' },
    },
  };

  return (
    <BaseButton
      variant="icon"
      // Removed the Tailwind 'group' class since Framer Motion handles the hover state natively now
      className={`text-gray-700 !border-none  !hover:bg-transparent !h-10 !w-10 ${className} !p-0`}
      aria-label={reverse ? 'Go forward' : 'Go back'}
      {...props}
    >
      <motion.div
        className="relative h-full w-full flex items-center justify-center overflow-hidden"
        style={{ color: 'inherit' }}
        aria-hidden="true"
        initial="rest"
        animate="rest"
        whileHover="hover" // This triggers the 'hover' variant on all child motion elements
      >
        {/* Primary Arrow */}
        <motion.svg
          variants={primaryVariants}
          // Apply scale-x-[-1] to visually mirror the arrow when reversed
          className={`absolute h-full w-full inset-0 ${reverse ? 'scale-x-[-1]' : ''}`}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          preserveAspectRatio="xMidYMid meet"
        >
          <line x1="19" y1="12" x2="5" y2="12" />
          <polyline points="12 19 5 12 12 5" />
        </motion.svg>

        {/* Secondary Arrow */}
        <motion.svg
          variants={secondaryVariants}
          className={`absolute h-full w-full inset-0 ${reverse ? 'scale-x-[-1]' : ''}`}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          preserveAspectRatio="xMidYMid meet"
        >
          <line x1="19" y1="12" x2="5" y2="12" />
          <polyline points="12 19 5 12 12 5" />
        </motion.svg>
      </motion.div>
    </BaseButton>
  );
};
